import { useEffect, useState, type ReactNode } from 'react';
import { TriangleAlertIcon } from '../../../icons';

/** Consulta histórica do APP Consultor (spec 023): celular/tablet em retrato. */
export const SMALL_SCREEN_QUERY_PORTRAIT_TOUCH = '(pointer: coarse) and (orientation: portrait)';

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    setMatches(mql.matches);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

export interface SmallScreenWarningProps {
  title?: string;
  body?: string;
}

export function SmallScreenWarning({
  title = 'Parece que a sua tela é um pouco pequena.',
  body = 'Para usar a plataforma, acesse pelo computador ou vire o seu tablet na horizontal.',
}: SmallScreenWarningProps) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      data-slot="small-screen-warning"
      className="flex min-h-dvh items-center justify-center bg-background px-5 py-12"
    >
      <div className="flex w-full max-w-[335px] flex-col items-center gap-6">
        <div className="flex size-16 items-center justify-center rounded-full bg-muted">
          <TriangleAlertIcon className="size-6 text-foreground" aria-hidden />
        </div>
        <div className="flex flex-col items-center gap-1.5 text-center">
          <h1 className="text-2xl font-semibold leading-none tracking-tight text-foreground">
            {title}
          </h1>
          <p className="text-sm leading-5 text-accent-foreground">{body}</p>
        </div>
      </div>
    </div>
  );
}

export interface SmallScreenGateProps extends SmallScreenWarningProps {
  children: ReactNode;
  /** Quando a tela é "pequena". Padrão: o do APP Consultor. */
  query?: string;
  /**
   * Mantém o app MONTADO (escondido) enquanto o aviso aparece, para que o que
   * foi preenchido sobreviva a um redimensionamento (DEV-259). Padrão: desmonta.
   */
  preserveChildren?: boolean;
}

/** Aviso de tela pequena para qualquer tela do app (DEV-259 / spec 023). */
export function SmallScreenGate({
  children,
  query = SMALL_SCREEN_QUERY_PORTRAIT_TOUCH,
  preserveChildren = false,
  title,
  body,
}: SmallScreenGateProps) {
  const small = useMediaQuery(query);
  if (!preserveChildren) {
    return small ? <SmallScreenWarning title={title} body={body} /> : <>{children}</>;
  }
  return (
    <>
      {small ? <SmallScreenWarning title={title} body={body} /> : null}
      <div hidden={small} aria-hidden={small || undefined} data-slot="small-screen-gate-content">
        {children}
      </div>
    </>
  );
}
